// Building manage panel — for a BUSINESS the owner reviews job APPLICATIONS (approve/reject) and fires
// staff; for a HOUSE (apartment) the owner hands out / takes back door KEYS instead. Both list the
// building for sale. Driven entirely from world3d: it passes the current view and the callbacks.
// Re-rendered live as the synced building state changes.

import { injectTheme } from "./theme.js";

export interface ManageView {
  buildingId: string;
  buildingName: string;
  cap: number;
  /** A HOUSE (apartment home): shows door KEYS instead of staff/applications. */
  isHouse: boolean;
  employees: { id: string; name: string }[];
  applicants: { id: string; name: string }[];
  /** Door-key holders (houses) — may enter while locked + lock/unlock. */
  keyholders: { id: string; name: string }[];
  forSale: boolean;  // currently listed on the market
  salePrice: number; // the owner's asking price while listed
  /** Live auction (endsAt in epoch ms), or null. While set, the direct sale listing is disabled. */
  auction: { endsAt: number; startPrice: number; minIncrement: number; bid: number; bidderName: string } | null;
  /** This viewer may START an auction here (admin, no auction running). */
  canAuction: boolean;
  /** This viewer may CANCEL the live auction (admin) — the top bidder's hold is refunded. */
  canCancelAuction: boolean;
}
export interface ManageActions {
  approve: (characterId: string) => void; // hire a pending applicant
  reject: (characterId: string) => void;  // dismiss a pending applicant
  fire: (characterId: string) => void;    // remove a hired employee
  giveKey: (characterName: string) => void; // hand a door key to an online player (houses)
  revokeKey: (characterId: string) => void; // take a keyholder's door key back (houses)
  list: (price: number) => void;          // put up for sale at a custom price
  unlist: () => void;                     // take it off the market
  /** Admin: put the building up for auction (duration in hours, opening price, minimum raise). */
  startAuction: (durationHours: number, startPrice: number, minIncrement: number) => void;
  /** Admin: call off the live auction (the top bidder's held money is refunded). */
  cancelAuction: () => void;
  close: () => void;
}

const STYLE_ID = "ghostland-manage-style";
const OVERLAY_ID = "gl-manage";
const CSS = `
#${OVERLAY_ID} { position: fixed; inset: 0; z-index: 36; display: none; align-items: center; justify-content: center;
  background: rgba(3,1,6,.55); font-family: ui-monospace, "SFMono-Regular", Menlo, monospace; }
#${OVERLAY_ID}.open { display: flex; }
#${OVERLAY_ID} .gl-mg-card { width: 380px; max-height: 78vh; overflow: hidden; display: flex; flex-direction: column;
  background: var(--gl-panel, #14101c); border: 1px solid var(--gl-border, #3a3450); border-radius: 10px;
  box-shadow: 0 18px 60px rgba(0,0,0,.6); color: var(--gl-text, #f2f0f7); }
#${OVERLAY_ID} .gl-mg-head { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px;
  border-bottom: 1px solid var(--gl-border, #3a3450); }
#${OVERLAY_ID} .gl-mg-title { font-size: 14px; letter-spacing: .04em; }
#${OVERLAY_ID} .gl-mg-x { cursor: pointer; color: var(--gl-muted, #9a93b0); font-size: 16px; line-height: 1; padding: 2px 6px; }
#${OVERLAY_ID} .gl-mg-x:hover { color: var(--gl-text, #fff); }
#${OVERLAY_ID} .gl-mg-body { overflow-y: auto; padding: 12px 16px 16px; }
#${OVERLAY_ID} .gl-mg-sect { font-size: 10px; letter-spacing: .14em; text-transform: uppercase; color: var(--gl-muted, #9a93b0); margin: 10px 0 6px; }
#${OVERLAY_ID} .gl-mg-row { display: flex; align-items: center; justify-content: space-between; gap: 8px;
  padding: 7px 10px; border-radius: 7px; background: rgba(255,255,255,.03); margin-bottom: 5px; }
#${OVERLAY_ID} .gl-mg-name { font-size: 13px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
#${OVERLAY_ID} .gl-mg-btn { cursor: pointer; font-size: 11px; letter-spacing: .04em; padding: 4px 12px; border-radius: 6px;
  border: 1px solid var(--gl-border, #3a3450); background: var(--gl-panel-2, #1d1828); color: var(--gl-text, #f2f0f7); }
#${OVERLAY_ID} .gl-mg-approve:hover { border-color: #5ad06a; color: #8de89a; }
#${OVERLAY_ID} .gl-mg-reject:hover { border-color: #e0a05a; color: #f0c89a; }
#${OVERLAY_ID} .gl-mg-fire:hover { border-color: #e05a5a; color: #f29a9a; }
#${OVERLAY_ID} .gl-mg-acts { display: flex; gap: 6px; }
#${OVERLAY_ID} .gl-mg-empty { font-size: 12px; color: var(--gl-muted, #9a93b0); padding: 6px 2px; }
#${OVERLAY_ID} .gl-mg-sell { width: 100%; margin-top: 14px; cursor: pointer; font-size: 12px; letter-spacing: .04em;
  padding: 9px; border-radius: 7px; border: 1px solid #5a2630; background: rgba(120,30,40,.18); color: #f0a3ab; }
#${OVERLAY_ID} .gl-mg-sell:hover { border-color: #e05a5a; color: #f6c2c8; background: rgba(150,40,50,.28); }
#${OVERLAY_ID} .gl-mg-listed { margin-top: 14px; font-size: 12px; color: #e7c66a; text-align: center; padding: 7px 0; }
#${OVERLAY_ID} .gl-mg-unlist, #${OVERLAY_ID} .gl-mg-givekey { width: 100%; margin-top: 8px; cursor: pointer; font-size: 12px; letter-spacing: .04em;
  padding: 9px; border-radius: 7px; border: 1px solid var(--gl-border, #3a3450); background: var(--gl-panel-2, #1d1828); color: var(--gl-text, #f2f0f7); }
#${OVERLAY_ID} .gl-mg-unlist:hover, #${OVERLAY_ID} .gl-mg-givekey:hover { border-color: #8a83a0; }
#${OVERLAY_ID} .gl-mg-auction { margin-top: 8px; font-size: 12px; color: #e7c66a; text-align: center; padding: 8px 6px;
  border: 1px solid #5a4a26; border-radius: 7px; background: rgba(120,95,30,.12); line-height: 1.7; }
#${OVERLAY_ID} .gl-mg-startauction { width: 100%; margin-top: 8px; cursor: pointer; font-size: 12px; letter-spacing: .04em;
  padding: 9px; border-radius: 7px; border: 1px solid #5a4a26; background: rgba(120,95,30,.14); color: #e7c66a; }
#${OVERLAY_ID} .gl-mg-startauction:hover { border-color: #e7c66a; }
#${OVERLAY_ID} .gl-mg-cancelauction { width: 100%; margin-top: 8px; cursor: pointer; font-size: 12px; letter-spacing: .04em;
  padding: 9px; border-radius: 7px; border: 1px solid #5a2630; background: rgba(120,30,40,.18); color: #f0a3ab; }
#${OVERLAY_ID} .gl-mg-cancelauction:hover { border-color: #e05a5a; color: #f6c2c8; }
#${OVERLAY_ID} .gl-mg-form { margin-top: 8px; padding: 10px; border: 1px solid #5a4a26; border-radius: 7px; background: rgba(120,95,30,.08); }
#${OVERLAY_ID} .gl-mg-field { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 7px; }
#${OVERLAY_ID} .gl-mg-field label { font-size: 11px; color: var(--gl-muted, #9a93b0); letter-spacing: .04em; }
#${OVERLAY_ID} .gl-mg-field input { width: 110px; padding: 5px 8px; font: inherit; font-size: 12px; text-align: right;
  color: var(--gl-text, #f2f0f7); background: var(--gl-panel-2, #1d1828); border: 1px solid var(--gl-border, #3a3450); border-radius: 6px; outline: none; }
#${OVERLAY_ID} .gl-mg-field input:focus { border-color: #e7c66a; }
#${OVERLAY_ID} .gl-mg-form-err { font-size: 11px; color: #f29a9a; margin-bottom: 6px; display: none; }
#${OVERLAY_ID} .gl-mg-form-acts { display: flex; gap: 6px; }
#${OVERLAY_ID} .gl-mg-form-acts button { flex: 1; cursor: pointer; font-size: 12px; letter-spacing: .04em; padding: 8px;
  border-radius: 7px; border: 1px solid var(--gl-border, #3a3450); background: var(--gl-panel-2, #1d1828); color: var(--gl-text, #f2f0f7); }
#${OVERLAY_ID} .gl-mg-form-acts .gl-mg-form-go { border-color: #5a4a26; background: rgba(120,95,30,.18); color: #e7c66a; }
#${OVERLAY_ID} .gl-mg-form-acts .gl-mg-form-go:hover { border-color: #e7c66a; }
`;

/** Format the time left on an auction as "12h 34m 05s" (never negative). */
function fmtLeft(endsAt: number): string {
  const s = Math.max(0, Math.floor((endsAt - Date.now()) / 1000));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60;
  return `${h}h ${String(m).padStart(2, "0")}m ${String(ss).padStart(2, "0")}s`;
}

let overlay: HTMLDivElement | null = null;
let acts: ManageActions | null = null;
let openId = "";
let lastView: ManageView | null = null;
let countdown: number | null = null; // 1s re-render while a live auction is shown (keeps the clock moving)
let auctionFormOpen = false;         // the admin start-auction form is expanded

function ensure(): void {
  if (overlay) return;
  injectTheme();
  if (!document.getElementById(STYLE_ID)) {
    const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s);
  }
  overlay = document.createElement("div");
  overlay.id = OVERLAY_ID;
  overlay.innerHTML = `<div class="gl-mg-card"><div class="gl-mg-head"><div class="gl-mg-title"></div><div class="gl-mg-x">✕</div></div><div class="gl-mg-body"></div></div>`;
  document.body.appendChild(overlay);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) closeBuildingManage(); });
  overlay.querySelector(".gl-mg-x")!.addEventListener("click", () => closeBuildingManage());
}

function render(view: ManageView): void {
  if (!overlay) return;
  lastView = view;
  // Keep the auction countdown ticking while one is shown; stop when it isn't.
  if (view.auction && countdown === null) countdown = window.setInterval(() => { if (lastView) render(lastView); }, 1000);
  if (!view.auction && countdown !== null) { clearInterval(countdown); countdown = null; }
  overlay.querySelector(".gl-mg-title")!.textContent = view.isHouse
    ? `${view.buildingName} · Keys ${view.keyholders.length}`
    : `${view.buildingName} · Staff ${view.employees.length}/${view.cap}`;
  const body = overlay.querySelector(".gl-mg-body") as HTMLDivElement;
  const esc = (s: string): string => s.replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[c]!));
  const au = view.auction;
  // The admin start-auction FORM (duration / opening price / min raise) — expands in place of the
  // button. A live re-render (state sync) must not wipe what the admin already typed.
  const keep = (sel: string, fallback: string): string => {
    const el = overlay!.querySelector(sel) as HTMLInputElement | null;
    return el ? el.value : fallback;
  };
  const auctionForm = `<div class="gl-mg-form">
      <div class="gl-mg-field"><label>Duration (hours, 1–48)</label><input class="gl-mg-au-h" type="number" min="1" max="48" step="1" value="${keep(".gl-mg-au-h", "24")}"></div>
      <div class="gl-mg-field"><label>Opening price ($)</label><input class="gl-mg-au-s" type="number" min="1" step="1" value="${keep(".gl-mg-au-s", "1000")}"></div>
      <div class="gl-mg-field"><label>Minimum raise ($)</label><input class="gl-mg-au-i" type="number" min="1" step="1" value="${keep(".gl-mg-au-i", "100")}"></div>
      <div class="gl-mg-form-err"></div>
      <div class="gl-mg-form-acts"><button class="gl-mg-form-cancel">Back</button><button class="gl-mg-form-go">Start auction</button></div>
    </div>`;
  const saleBlock = au
    ? `<div class="gl-mg-auction">ON AUCTION — ends in ${fmtLeft(au.endsAt)}<br>${au.bid > 0 ? `Top bid $${au.bid.toLocaleString()} · ${esc(au.bidderName || "?")}` : `No bids yet — opens at $${au.startPrice.toLocaleString()}`}<br>Minimum raise $${au.minIncrement.toLocaleString()}</div>`
      + (view.canCancelAuction ? `<button class="gl-mg-cancelauction">Cancel auction — refund the top bidder (admin)</button>` : "")
    : (view.forSale
        ? `<div class="gl-mg-listed">Listed for sale — $${view.salePrice.toLocaleString()}</div><button class="gl-mg-unlist">Take off the market</button>`
        : `<button class="gl-mg-sell">List ${esc(view.buildingName)} for sale…</button>`)
      + (view.canAuction ? (auctionFormOpen ? auctionForm : `<button class="gl-mg-startauction">Put up for auction… (admin)</button>`) : "");
  if (view.isHouse) {
    // HOUSE: door keys instead of staff. A keyholder may enter while locked + lock/unlock the door.
    const keyRows = view.keyholders.length
      ? view.keyholders.map((k) => `<div class="gl-mg-row"><span class="gl-mg-name">${esc(k.name)}</span><span class="gl-mg-btn gl-mg-fire" data-revoke="${k.id}">Take back</span></div>`).join("")
      : `<div class="gl-mg-empty">No one else has a key.</div>`;
    body.innerHTML = `<div class="gl-mg-sect">Door keys</div>${keyRows}<button class="gl-mg-givekey">Give a key to a player…</button><div class="gl-mg-sect">For sale</div>${saleBlock}`;
    body.querySelectorAll<HTMLElement>("[data-revoke]").forEach((el) => el.onclick = () => acts?.revokeKey(el.dataset.revoke!));
    const giveBtn = body.querySelector(".gl-mg-givekey") as HTMLElement | null;
    if (giveBtn) giveBtn.onclick = () => {
      const name = window.prompt("Give a door key to which player? (they must be online)");
      if (name && name.trim()) acts?.giveKey(name.trim());
    };
  } else {
    const empRows = view.employees.length
      ? view.employees.map((e) => `<div class="gl-mg-row"><span class="gl-mg-name">${esc(e.name)}</span><span class="gl-mg-btn gl-mg-fire" data-fire="${e.id}">Remove</span></div>`).join("")
      : `<div class="gl-mg-empty">No staff hired yet.</div>`;
    const full = view.employees.length >= view.cap;
    const appRows = view.applicants.length
      ? view.applicants.map((a) => `<div class="gl-mg-row"><span class="gl-mg-name">${esc(a.name)}</span><span class="gl-mg-acts"><span class="gl-mg-btn gl-mg-approve" data-approve="${a.id}" ${full ? 'style="opacity:.4;pointer-events:none"' : ""}>Hire</span><span class="gl-mg-btn gl-mg-reject" data-reject="${a.id}">Reject</span></span></div>`).join("")
      : `<div class="gl-mg-empty">No pending applications.</div>`;
    body.innerHTML = `<div class="gl-mg-sect">Staff ${view.employees.length}/${view.cap}</div>${empRows}<div class="gl-mg-sect">${full ? "Applications (staff full)" : "Applications"}</div>${appRows}<div class="gl-mg-sect">For sale</div>${saleBlock}`;
    body.querySelectorAll<HTMLElement>("[data-fire]").forEach((el) => el.onclick = () => acts?.fire(el.dataset.fire!));
    body.querySelectorAll<HTMLElement>("[data-approve]").forEach((el) => el.onclick = () => acts?.approve(el.dataset.approve!));
    body.querySelectorAll<HTMLElement>("[data-reject]").forEach((el) => el.onclick = () => acts?.reject(el.dataset.reject!));
  }
  const sellBtn = body.querySelector(".gl-mg-sell") as HTMLElement | null;
  if (sellBtn) sellBtn.onclick = () => {
    const raw = window.prompt(`List ${view.buildingName} for sale — asking price ($)?`, String(view.salePrice || ""));
    if (raw === null) return; // cancelled
    const price = Math.max(0, Math.floor(Number(raw)));
    if (!Number.isFinite(price) || price <= 0) { window.alert("Enter a price greater than 0."); return; }
    acts?.list(price);
  };
  const unlistBtn = body.querySelector(".gl-mg-unlist") as HTMLElement | null;
  if (unlistBtn) unlistBtn.onclick = () => acts?.unlist();
  const auctionBtn = body.querySelector(".gl-mg-startauction") as HTMLElement | null;
  if (auctionBtn) auctionBtn.onclick = () => { auctionFormOpen = true; if (lastView) render(lastView); };
  const cancelAuctionBtn = body.querySelector(".gl-mg-cancelauction") as HTMLElement | null;
  if (cancelAuctionBtn) cancelAuctionBtn.onclick = () => acts?.cancelAuction();
  // Start-auction form: validate inline (no browser prompts) and send.
  const formEl = body.querySelector(".gl-mg-form") as HTMLDivElement | null;
  if (formEl) {
    const err = formEl.querySelector(".gl-mg-form-err") as HTMLDivElement;
    (formEl.querySelector(".gl-mg-form-cancel") as HTMLElement).onclick = () => { auctionFormOpen = false; if (lastView) render(lastView); };
    (formEl.querySelector(".gl-mg-form-go") as HTMLElement).onclick = () => {
      const num = (sel: string): number => Math.floor(Number((formEl.querySelector(sel) as HTMLInputElement).value));
      const hours = num(".gl-mg-au-h"), start = num(".gl-mg-au-s"), inc = num(".gl-mg-au-i");
      const fail = (text: string): void => { err.textContent = text; err.style.display = "block"; };
      if (!Number.isFinite(hours) || hours < 1 || hours > 48) { fail("Duration must be 1–48 hours."); return; }
      if (!Number.isFinite(start) || start < 1) { fail("Opening price must be at least $1."); return; }
      if (!Number.isFinite(inc) || inc < 1) { fail("Minimum raise must be at least $1."); return; }
      auctionFormOpen = false;
      acts?.startAuction(hours, start, inc);
    };
  }
}

/** Open the staff panel for a building. */
export function openBuildingManage(view: ManageView, actions: ManageActions): void {
  ensure();
  acts = actions; openId = view.buildingId; auctionFormOpen = false;
  render(view);
  overlay!.classList.add("open");
}
/** Re-render with fresh data (called when the synced building / online players change). */
export function refreshBuildingManage(view: ManageView): void {
  if (overlay?.classList.contains("open") && view.buildingId === openId) render(view);
}
export function closeBuildingManage(): void {
  if (!overlay?.classList.contains("open")) return;
  if (countdown !== null) { clearInterval(countdown); countdown = null; }
  lastView = null; auctionFormOpen = false;
  overlay.classList.remove("open"); openId = ""; const a = acts; acts = null; a?.close();
}
export function isBuildingManageOpen(): boolean { return !!overlay?.classList.contains("open"); }
export function buildingManageId(): string { return openId; }
